import React, { useState, useEffect } from 'react';
import apiService from '../services/api.service';

const SupplierCategories = () => {
  // States for category management
  const [categories, setCategories] = useState([]);
  const [currentCategory, setCurrentCategory] = useState({
    id: null,
    name: ''
  });
  
  // UI states
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch categories with filters
  const fetchCategories = async () => {
    try {
      console.log('Loading categories...');
      const filters = {};
      if (searchTerm) filters.name = searchTerm;
      
      const response = await apiService.getSupplierCategories(filters);
      console.log('Category response:', response);
      setCategories(response.data);
      setError(null);
    } catch (err) {
      console.error('Error loading categories:', err);
      setError('Failed to load categories: ' + 
        (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchCategories();
  }, []);

  // Reload categories when search term changes
  useEffect(() => {
    fetchCategories();
  }, [searchTerm]);

  // Handle category form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentCategory({
      ...currentCategory,
      [name]: value
    });
  };

  // Submit category form
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Since there's no explicit category creation endpoint in the API,
      // we'll create a supplier with this category to add it to the system
      // This approach leverages the existing backend which creates categories
      // when they're added to suppliers
      
      if (modalMode === 'add') {
        // For new categories, we need to create a temporary supplier that uses this category
        // The supplier controller will create the category if it doesn't exist
        const tempSupplier = {
          name: `Temp-${Date.now()}`, // Unique name to avoid conflicts
          isActive: false, // Set as inactive so it's not visible
          categories: [currentCategory.name] // The new category
        };
        
        await apiService.createSupplier(tempSupplier);
        
        // Now delete the temporary supplier but the category will remain
        const response = await apiService.getSuppliers({name: tempSupplier.name});
        if (response.data && response.data.length > 0) {
          await apiService.deleteSupplier(response.data[0].id);
        }
      } else {
        // Category editing isn't directly supported in the API
        // This would require backend changes
        console.warn('Category updating requires backend changes');
      }

      // Success actions
      setModalVisible(false);
      resetForm();
      fetchCategories();
    } catch (err) {
      console.error('Error saving category:', err);
      setError('Failed to save category: ' + 
        (err.response?.data?.error || err.message));
    }
  };

  // Delete category
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category?')) {
      return;
    }

    try {
      await apiService.deleteSupplierCategory(id);
      fetchCategories();
    } catch (err) {
      console.error('Error deleting category:', err);
      setError('Failed to delete category: ' + 
        (err.response?.data?.error || err.message));
    }
  };

  // Edit category
  const handleEdit = (category) => {
    setCurrentCategory({
      id: category.id,
      name: category.name
    });
    
    setModalMode('edit');
    setModalVisible(true);
  };

  // Open add category modal
  const handleAdd = () => {
    resetForm();
    setModalMode('add');
    setModalVisible(true);
  };

  // Reset category form
  const resetForm = () => {
    setCurrentCategory({
      id: null,
      name: ''
    });
  };

  return (
    <div className="tab-content">
      <div className="content-header">
        <h3>Supplier Categories</h3>
        <button 
          className="add-button"
          onClick={handleAdd}
        >
          <span className="icon">+</span>
          <span className="button-text">Add New Category</span>
        </button>
      </div>

      <div className="filters">
        <div className="search-box">
          <input 
            type="text" 
            placeholder="Search categories..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : (
        <div className="categories-list">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Suppliers Count</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {categories.length === 0 ? (
                <tr>
                  <td colSpan="3" className="no-data">No categories found.</td>
                </tr>
              ) : (
                categories.map(category => (
                  <tr key={category.id}>
                    <td>{category.name}</td>
                    <td>{category.Suppliers ? category.Suppliers.length : 0}</td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="edit-button"
                          onClick={() => handleEdit(category)}
                          title="Edit"
                        >
                          <span className="icon">✎</span>
                        </button>
                        <button 
                          className="delete-button"
                          onClick={() => handleDelete(category.id)}
                          title="Delete"
                        >
                          <span className="icon">✕</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Category Modal */}
      {modalVisible && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{modalMode === 'add' ? 'Add New Category' : 'Edit Category'}</h3>
              <button 
                className="close-button"
                onClick={() => setModalVisible(false)}
              >
                &times;
              </button>
            </div>
            
            <form className="form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="categoryName">Category Name *</label>
                <input 
                  type="text" 
                  id="categoryName" 
                  name="name" 
                  value={currentCategory.name} 
                  onChange={handleInputChange} 
                  required 
                  placeholder="e.g., Coffee, Croissant, Desserts"
                />
              </div>
              
              <div className="form-actions">
                <button 
                  type="button" 
                  className="cancel-button"
                  onClick={() => setModalVisible(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="save-button"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierCategories;